import { FC, useEffect, useState } from 'react';

import RichText from '@eneco/components/RichText/RichText';
import { TrackedDialog } from '@eneco/components/TrackedDialog/TrackedDialog';
import { Header, Layout, NextButton } from '@eneco/flows/components';
import { useFlowHooks } from '@eneco/flows/utils/FlowProvider';
import { typedYupResolver } from '@eneco/hooks/form';
import { useFormatter } from '@eneco/i18n';
import { NotificationBox, RadioGroup, Stack, Text, TextLink, RadioTile, Grid, Stretch } from '@eneco/sparky';
import { usePlaceholderContent } from '@sitecore/common';
import { AddressDetailsStepRendering } from '@sitecore/types/AddressDetailsStep';
import { useForm, Controller } from 'react-hook-form';
import * as yup from 'yup';

import { FlowConsumerContextEnergy } from '../../../context';
import { useFlowNavigation } from '../../hooks';

interface FormFields {
  isLivingOrWorkingResidence: string;
  hasResidencePurpose?: string;
}

export const AddressDetailsStep: FC = () => {
  const { useFlowSelector } = useFlowHooks<FlowConsumerContextEnergy>();
  const flowState = useFlowSelector(state => state);
  const { updateContextAndGoToNextStep } = useFlowNavigation();
  const { address } = useFormatter();

  const {
    context: {
      isLivingOrWorkingResidence,
      hasResidencePurpose,
      postalCode,
      houseNumber,
      houseNumberSuffix,
      street,
      city,
    },
  } = flowState;

  const { AddressDetailsStep: { fields: textualData } = {} } = usePlaceholderContent<{
    AddressDetailsStep: AddressDetailsStepRendering;
  }>();

  const [shouldShowExtraQuestion, setShouldShowExtraQuestion] = useState(
    isLivingOrWorkingResidence !== undefined && !isLivingOrWorkingResidence,
  );

  const formSchema = yup.object({
    isLivingOrWorkingResidence: yup
      .string()
      .required(textualData?.choice?.isLivingOrWorkingResidenceRadio?.value?.requiredMessage),

    ...(shouldShowExtraQuestion && {
      hasResidencePurpose: yup.string().required(textualData?.choice?.hasResidencePurposeRadio?.value?.requiredMessage),
    }),
  });

  const {
    handleSubmit,
    control,
    getValues,
    watch,
    formState: { errors, isSubmitSuccessful },
    unregister,
  } = useForm<FormFields>({
    mode: 'onSubmit',
    defaultValues: {
      isLivingOrWorkingResidence: isLivingOrWorkingResidence?.toString(),
      ...(shouldShowExtraQuestion && {
        hasResidencePurpose: hasResidencePurpose?.toString(),
      }),
    },
    resolver: typedYupResolver<FormFields>(formSchema),
  });

  const answerOne = watch('isLivingOrWorkingResidence');
  const answerTwo = watch('hasResidencePurpose');

  const [shouldShowNotificationOne, setShouldShowNotificationOne] = useState(
    isLivingOrWorkingResidence !== undefined && isLivingOrWorkingResidence,
  );
  const [shouldShowNotificationTwo, setShouldShowNotificationTwo] = useState(
    hasResidencePurpose !== undefined && hasResidencePurpose,
  );
  const [shouldShowNotificationThree, setShouldShowNotificationThree] = useState(
    hasResidencePurpose !== undefined && !hasResidencePurpose,
  );

  useEffect(() => {
    if (isSubmitSuccessful) {
      const formData = getValues();

      updateContextAndGoToNextStep({
        isLivingOrWorkingResidence: formData.isLivingOrWorkingResidence === 'true',
        hasResidencePurpose: formData.hasResidencePurpose
          ? formData.hasResidencePurpose === 'true'
          : formData.isLivingOrWorkingResidence === 'true',
      });
    }
  }, [isSubmitSuccessful]);

  useEffect(() => {
    if (answerOne === 'true') {
      unregister('hasResidencePurpose');
    }

    setShouldShowNotificationOne(answerOne === 'true');
    setShouldShowExtraQuestion(answerOne === 'false');
  }, [answerOne, unregister]);

  useEffect(() => {
    setShouldShowNotificationTwo(answerTwo === 'true');
    setShouldShowNotificationThree(answerTwo === 'false');
  }, [answerTwo]);

  return (
    <Layout.Content variant="A" handleSubmit={handleSubmit}>
      <Header>
        <Header.Title>{textualData?.content?.title?.value}</Header.Title>
        {textualData?.content?.text?.value && (
          <Header.Text>
            {textualData.content.text.value}{' '}
            <TrackedDialog
              title={textualData?.info?.residenceFunctionDialog?.value?.title}
              trigger={<TextLink>{textualData?.info?.residenceFunctionDialog?.value?.triggerText}</TextLink>}>
              <RichText html={textualData?.info?.residenceFunctionDialog?.value?.content} />
            </TrackedDialog>
          </Header.Text>
        )}
      </Header>

      {textualData?.content?.notification?.value?.content && (
        <NotificationBox
          isAlert={false}
          variant={textualData?.content?.notification?.value?.variant}
          title={textualData?.content?.notification?.value?.title}
          text={<RichText html={textualData?.content?.notification?.value?.content} />}
        />
      )}

      <Stack.Item>
        <Text size="BodyS" weight="bold">
          {textualData?.content?.addressText?.value}
        </Text>
        <Text>{address.long({ street, houseNumber, houseNumberSuffix, postalCode, city })}</Text>
      </Stack.Item>

      <Controller
        control={control}
        name="isLivingOrWorkingResidence"
        render={({ field: { onChange, value, name } }) => (
          <RadioGroup
            name={name}
            label={textualData?.choice?.isLivingOrWorkingResidenceRadio?.value?.label ?? ''}
            value={value}
            onValueChange={onChange}
            error={errors['isLivingOrWorkingResidence']?.message}>
            <Stretch>
              <Grid gridTemplateColumns="1fr 1fr" gap="4">
                {textualData?.choice?.isLivingOrWorkingResidenceRadio?.value?.options?.map(option => (
                  <RadioTile value={option.name} key={option.name}>
                    {option.label}
                  </RadioTile>
                ))}
              </Grid>
            </Stretch>
          </RadioGroup>
        )}
      />

      {shouldShowExtraQuestion && (
        <Controller
          control={control}
          name="hasResidencePurpose"
          render={({ field: { onChange, value, name } }) => (
            <RadioGroup
              name={name}
              label={textualData?.choice?.hasResidencePurposeRadio?.value?.label ?? ''}
              value={value}
              onValueChange={onChange}
              error={errors['hasResidencePurpose']?.message}>
              <Stretch>
                <Grid gridTemplateColumns="1fr 1fr" gap="4">
                  {textualData?.choice?.hasResidencePurposeRadio?.value?.options?.map(option => (
                    <RadioTile value={option.name} key={option.name}>
                      {option.label}
                    </RadioTile>
                  ))}
                </Grid>
              </Stretch>
            </RadioGroup>
          )}
        />
      )}

      {(shouldShowNotificationOne || shouldShowNotificationTwo || shouldShowNotificationThree) && (
        <Stack.Item>
          <NotificationBox
            isAlert={false}
            variant={
              shouldShowNotificationThree
                ? textualData?.info?.residenceFunctionNotification?.value?.variant
                : textualData?.info?.residenceFunctionConfirmationNotification?.value?.variant
            }
            title={
              shouldShowNotificationThree
                ? textualData?.info?.residenceFunctionNotification?.value?.title
                : textualData?.info?.residenceFunctionConfirmationNotification?.value?.title
            }
            text={
              <Text size="BodyS">
                <RichText
                  html={
                    shouldShowNotificationThree
                      ? textualData?.info?.residenceFunctionNotification?.value?.content
                      : textualData?.info?.residenceFunctionConfirmationNotification?.value?.content
                  }
                />
              </Text>
            }
          />
        </Stack.Item>
      )}

      <NextButton isLoading={isSubmitSuccessful}>{textualData?.content?.nextStepText?.value}</NextButton>
    </Layout.Content>
  );
};
